<template>
  <div class="vc-root" :style="{ '--brand': settings.kiosk_brand_colour || '#004AAD' }">

    <!-- ── Loading ─────────────────────────────────────────────────────────── -->
    <div v-if="loadingCompany" class="vc-splash">
      <div class="vc-splash-ring"></div>
      <p>Loading…</p>
    </div>

    <!-- ── Error ───────────────────────────────────────────────────────────── -->
    <div v-else-if="companyError" class="vc-error-screen">
      <div class="vc-error-box">
        <div class="vc-error-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        </div>
        <h2>Link not found</h2>
        <p>This visitor check-in link is invalid or has been disabled. Please ask your host for the correct link.</p>
      </div>
    </div>

    <!-- ── Kiosk disabled ─────────────────────────────────────────────────── -->
    <div v-else-if="kioskDisabled" class="vc-error-screen">
      <div class="vc-error-box">
        <div class="vc-error-icon" style="background:#fef3c7;color:#d97706;">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        </div>
        <h2>Kiosk Currently Unavailable</h2>
        <p>Self-service check-in has been temporarily disabled. Please check in at the reception desk.</p>
      </div>
    </div>

    <!-- ── Visitor Pass ─────────────────────────────────────────────────────── -->
    <div v-else-if="pass" class="vc-pass-screen">
      <div class="vc-pass-success-header">
        <div class="vc-pass-success-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
        </div>
        <div>
          <div class="vc-pass-success-title">You're checked in!</div>
          <div class="vc-pass-success-sub">Show this pass to reception on your way out.</div>
        </div>
      </div>

      <div class="vc-badge" ref="passCard">
        <!-- Left accent strip -->
        <div class="vc-badge-strip"></div>

        <!-- Badge content -->
        <div class="vc-badge-content">
          <!-- Top row: company + VISITOR label -->
          <div class="vc-badge-top">
            <div class="vc-badge-company">
              <img v-if="company.logo" :src="company.logo" class="vc-badge-logo" alt="" />
              <span class="vc-badge-company-name">{{ company.name }}</span>
            </div>
            <span class="vc-badge-type-label">VISITOR</span>
          </div>

          <!-- Photo + Name row -->
          <div class="vc-badge-identity">
            <div class="vc-badge-photo-wrap">
              <img v-if="pass.visitor?.photo_url" :src="pass.visitor.photo_url" class="vc-badge-photo" alt="" />
              <div v-else class="vc-badge-initials">{{ passInitials }}</div>
            </div>
            <div class="vc-badge-name-block">
              <div class="vc-badge-name">{{ pass.visitor?.first_name }} {{ pass.visitor?.last_name }}</div>
              <div v-if="pass.visitor?.company" class="vc-badge-org">{{ pass.visitor.company }}</div>
              <div v-if="pass.visitor?.email" class="vc-badge-email">{{ pass.visitor.email }}</div>
            </div>
          </div>

          <!-- Badge number strip -->
          <div class="vc-badge-no-strip">
            <svg class="vc-badge-barcode-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="4" width="2" height="16"/><rect x="7" y="4" width="1" height="16"/><rect x="10" y="4" width="3" height="16"/><rect x="15" y="4" width="1" height="16"/><rect x="18" y="4" width="3" height="16"/></svg>
            <span class="vc-badge-no">{{ pass.badge_no }}</span>
          </div>

          <!-- Details grid -->
          <div class="vc-badge-details">
            <div class="vc-badge-detail">
              <span class="vc-bd-label">Visiting</span>
              <span class="vc-bd-value">{{ pass.host ? hostName(pass.host) : (pass.host_name || '—') }}</span>
            </div>
            <div class="vc-badge-detail">
              <span class="vc-bd-label">Purpose</span>
              <span class="vc-bd-value">{{ pass.purpose }}</span>
            </div>
            <div class="vc-badge-detail">
              <span class="vc-bd-label">Check-in time</span>
              <span class="vc-bd-value">{{ fmtDatetime(pass.checked_in_at) }}</span>
            </div>
          </div>

          <!-- Footer instruction -->
          <div class="vc-badge-instruction">
            Please wear this pass visibly at all times. Present to reception when leaving.
          </div>
        </div>
      </div>

      <div class="vc-pass-actions">
        <button class="vc-btn-print" @click="printPass">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
          Print Pass
        </button>
        <button class="vc-btn-checkout" @click="checkOutFromPass" :disabled="checkingOut">
          <span v-if="checkingOut" class="vc-submit-spinner" style="border-top-color:#fff;width:14px;height:14px;border-width:2px;"></span>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          {{ checkingOut ? 'Checking out…' : 'Check Out' }}
        </button>
        <button class="vc-btn-ghost" @click="startOver">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          New Check-In
        </button>
      </div>
    </div>

    <!-- ── Checked-out confirmation ───────────────────────────────────────── -->
    <div v-else-if="checkedOut" class="vc-pass-screen">
      <div class="vc-pass-success-header">
        <div class="vc-pass-success-icon" style="background:rgba(255,255,255,.2);">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.5"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </div>
        <div>
          <div class="vc-pass-success-title">Checked out!</div>
          <div class="vc-pass-success-sub">Thank you for your visit. Please return your badge to reception.</div>
        </div>
      </div>
      <div class="vc-badge" ref="checkoutCard">
        <div class="vc-badge-strip" style="background:linear-gradient(180deg,#16a34a 0%,#15803d 100%);"></div>
        <div class="vc-badge-content">
          <div class="vc-badge-top">
            <div class="vc-badge-company">
              <img v-if="company.logo" :src="company.logo" class="vc-badge-logo" alt="" />
              <span class="vc-badge-company-name">{{ company.name }}</span>
            </div>
            <span class="vc-badge-type-label" style="background:#16a34a;">CHECKED OUT</span>
          </div>
          <div class="vc-badge-identity">
            <div class="vc-badge-photo-wrap">
              <img v-if="checkedOut.visitor?.photo_url" :src="checkedOut.visitor.photo_url" class="vc-badge-photo" alt="" />
              <div v-else class="vc-badge-initials" style="background:linear-gradient(135deg,#16a34a 0%,#15803d 100%);">{{ checkoutInitials }}</div>
            </div>
            <div class="vc-badge-name-block">
              <div class="vc-badge-name">{{ checkedOut.visitor?.first_name }} {{ checkedOut.visitor?.last_name }}</div>
              <div v-if="checkedOut.visitor?.company" class="vc-badge-org">{{ checkedOut.visitor.company }}</div>
            </div>
          </div>
          <div class="vc-badge-details">
            <div class="vc-badge-detail">
              <span class="vc-bd-label">Checked In</span>
              <span class="vc-bd-value">{{ fmtDatetime(checkedOut.checked_in_at) }}</span>
            </div>
            <div class="vc-badge-detail">
              <span class="vc-bd-label">Checked Out</span>
              <span class="vc-bd-value">{{ fmtDatetime(checkedOut.checked_out_at) }}</span>
            </div>
          </div>
          <div class="vc-badge-instruction">Thank you for visiting {{ company.name }}. Have a safe journey!</div>
        </div>
      </div>
      <div class="vc-pass-actions">
        <button class="vc-btn-ghost" @click="startOver">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 109-9M3 3v6h6"/></svg>
          Done
        </button>
      </div>
    </div>

    <!-- ── Pre-registration reference flow ──────────────────────────────────── -->
    <div v-else-if="showRefFlow" class="vc-ref-screen">
      <div class="vc-ref-card">
        <div class="vc-ref-icon-wrap">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#004AAD" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3M17 14v3M14 17h3M20 17v3M17 20h3"/></svg>
        </div>
        <h2 class="vc-ref-title">Use Your Reference Code</h2>
        <p class="vc-ref-sub">Your host sent you a check-in link or QR code with a reference. Paste it below to get your pass instantly.</p>

        <div v-if="!refVisit" class="vc-ref-form">
          <button class="vc-qr-trigger" @click="startQrScan('ref')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3M17 14v3M14 17h3M20 17v3M17 20h3"/></svg>
            Scan QR Code
          </button>
          <div class="vc-flow-divider"><span>or enter code</span></div>
          <div class="vc-ref-field">
            <label class="vc-ref-label">Reference Code</label>
            <input v-model="refToken" class="vc-ref-input" placeholder="Paste your reference code here…" @keyup.enter="lookupRef" autocomplete="off" />
          </div>
          <div v-if="refError" class="vc-ref-error">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ refError }}
          </div>
          <button class="vc-ref-btn" @click="lookupRef" :disabled="!refToken.trim() || refLooking">
            <span v-if="refLooking" class="vc-submit-spinner" style="border-top-color:#fff;"></span>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            {{ refLooking ? 'Looking up…' : 'Find My Registration' }}
          </button>
          <button class="vc-ref-back" @click="backToWelcome">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Sign in as a new visitor instead
          </button>
        </div>

        <div v-else class="vc-ref-preview">
          <div class="vc-ref-vis-avatar">{{ refVisitInitials }}</div>
          <div class="vc-ref-vis-name">{{ refVisit.visitor_name }}</div>
          <div v-if="refVisit.visitor_company" class="vc-ref-vis-sub">{{ refVisit.visitor_company }}</div>
          <div class="vc-ref-details">
            <div class="vc-ref-detail-row">
              <span class="vc-ref-detail-label">Visiting</span>
              <span class="vc-ref-detail-val">{{ refVisit.host_name }}</span>
            </div>
            <div class="vc-ref-detail-row">
              <span class="vc-ref-detail-label">Purpose</span>
              <span class="vc-ref-detail-val">{{ refVisit.purpose }}</span>
            </div>
            <div class="vc-ref-detail-row">
              <span class="vc-ref-detail-label">Scheduled</span>
              <span class="vc-ref-detail-val">{{ fmtDatetime(refVisit.scheduled_at) }}</span>
            </div>
          </div>
          <div v-if="refError" class="vc-ref-error">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ refError }}
          </div>
          <button class="vc-ref-btn" @click="activateRef" :disabled="refActivating">
            <span v-if="refActivating" class="vc-submit-spinner" style="border-top-color:#fff;"></span>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12l5 5L20 7"/></svg>
            {{ refActivating ? 'Activating…' : 'Confirm &amp; Get My Pass' }}
          </button>
          <button class="vc-ref-back" @click="refVisit = null; refToken = ''; refError = ''">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Try a different code
          </button>
        </div>
      </div>
      <p class="vc-welcome-powered" style="position:fixed;bottom:16px;left:0;right:0;text-align:center;">Powered by <strong>Mapiibs</strong></p>
    </div>

    <!-- ── Check-out flow ────────────────────────────────────────────────────── -->
    <div v-else-if="showCheckOutFlow" class="vc-ref-screen">
      <div class="vc-ref-card">
        <div class="vc-ref-icon-wrap" style="background:#dcfce7;">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="1.8"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
        </div>
        <h2 class="vc-ref-title">Check Out</h2>
        <p class="vc-ref-sub">Scan the QR code on your visitor badge, or enter the badge number.</p>
        <div class="vc-ref-form">
          <button class="vc-qr-trigger" style="border-color:#16a34a;color:#16a34a;" @click="startQrScan('checkout')">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3M17 14v3M14 17h3M20 17v3M17 20h3"/></svg>
            Scan Badge QR Code
          </button>
          <div class="vc-flow-divider"><span>or enter badge number</span></div>
          <div class="vc-ref-field">
            <label class="vc-ref-label">Badge Number</label>
            <input v-model="checkOutBadge" class="vc-ref-input" placeholder="e.g. ABC00042" @keyup.enter="submitCheckOut" autocomplete="off" style="text-transform:uppercase;" />
          </div>
          <div v-if="checkOutError" class="vc-ref-error">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {{ checkOutError }}
          </div>
          <button class="vc-ref-btn" style="background:#16a34a;" @click="submitCheckOut" :disabled="!checkOutBadge.trim() || checkingOut">
            <span v-if="checkingOut" class="vc-submit-spinner" style="border-top-color:#fff;width:16px;height:16px;border-width:2px;"></span>
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12l5 5L20 7"/></svg>
            {{ checkingOut ? 'Checking out…' : 'Check Out' }}
          </button>
          <button class="vc-ref-back" @click="backToWelcome">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Back
          </button>
        </div>
      </div>
      <p class="vc-welcome-powered" style="position:fixed;bottom:16px;left:0;right:0;text-align:center;">Powered by <strong>Mapiibs</strong></p>
    </div>

    <!-- ── Welcome screen ──────────────────────────────────────────────────── -->
    <div v-else-if="showWelcome" class="vc-welcome">
      <div class="vc-welcome-bg">
        <div class="vc-welcome-deco vc-wd-1"></div>
        <div class="vc-welcome-deco vc-wd-2"></div>
        <div class="vc-welcome-deco vc-wd-3"></div>
      </div>
      <div class="vc-welcome-card">
        <div class="vc-welcome-logo-wrap">
          <img v-if="company.logo" :src="company.logo" class="vc-welcome-logo" alt="" />
          <div v-else class="vc-welcome-logo-fallback">
            {{ (company.name || 'V')[0].toUpperCase() }}
          </div>
        </div>
        <div class="vc-welcome-text">
          <p class="vc-welcome-eyebrow">You are visiting</p>
          <h1 class="vc-welcome-company">{{ company.name }}</h1>
          <p class="vc-welcome-tagline">Please sign in below. It only takes a minute.</p>
        </div>
        <button class="vc-welcome-cta" @click="showWelcome = false">
          <span>Sign In as Visitor</span>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
        </button>
        <div class="vc-welcome-divider"><span>or</span></div>
        <button class="vc-welcome-ref-btn" @click="startRefFlow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3M17 14v3M14 17h3M20 17v3M17 20h3"/></svg>
          I have a registration reference
        </button>
        <button class="vc-welcome-ref-btn" style="border-color:rgba(255,255,255,.15);margin-top:0;" @click="startCheckOutFlow">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          I'm checking out
        </button>
        <p class="vc-welcome-note">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          Your information is kept private and secure
        </p>
      </div>
      <p class="vc-welcome-powered">Powered by <strong>Mapiibs</strong></p>
    </div>

    <!-- ── Main form (split layout) ─────────────────────────────────────────── -->
    <div v-else class="vc-layout">

      <!-- Mobile-only compact header bar (hidden on desktop) -->
      <div class="vc-mobile-header">
        <div class="vc-mh-brand">
          <div class="vc-mh-logo-wrap">
            <img v-if="company.logo" :src="company.logo" class="vc-mh-logo" alt="" />
            <div v-else class="vc-mh-logo-fallback">{{ (company.name || 'V')[0].toUpperCase() }}</div>
          </div>
          <span class="vc-mh-name">{{ company.name || 'Visitor Check-In' }}</span>
        </div>
        <span class="vc-mh-badge">Visitor Check-In</span>
      </div>

      <!-- Left panel (hero) -->
      <aside class="vc-hero">
        <div class="vc-hero-inner">
          <div class="vc-hero-brand">
            <div class="vc-hero-logo-wrap">
              <img v-if="company.logo" :src="company.logo" class="vc-hero-logo" alt="" />
              <div v-else class="vc-hero-logo-placeholder">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.8)" stroke-width="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/></svg>
              </div>
            </div>
            <span class="vc-hero-company-name">{{ company.name || 'Welcome' }}</span>
          </div>

          <div class="vc-hero-text">
            <h1 class="vc-hero-heading">Visitor<br>Check-In</h1>
            <p class="vc-hero-desc">Quick, secure and contactless. Fill in your details and get your digital visitor pass instantly.</p>
          </div>

          <ul class="vc-hero-features">
            <li>
              <span class="vc-feat-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
              </span>
              Takes under 2 minutes
            </li>
            <li>
              <span class="vc-feat-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
              </span>
              Your data is kept private
            </li>
            <li>
              <span class="vc-feat-icon">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
              </span>
              Instant digital pass issued
            </li>
          </ul>
        </div>

        <!-- Decorative circles -->
        <div class="vc-deco-circle vc-deco-1"></div>
        <div class="vc-deco-circle vc-deco-2"></div>
      </aside>

      <!-- Right panel (form) -->
      <main class="vc-form-panel">
        <div class="vc-form-scroll">
          <div class="vc-form-header">
            <h2 class="vc-form-title">Sign in as a visitor</h2>
            <p class="vc-form-sub">All fields marked <span class="req">*</span> are required.</p>
          </div>

          <!-- Step 1: Your Details -->
          <div class="vc-step">
            <div class="vc-step-heading">
              <span class="vc-step-num">1</span>
              <span class="vc-step-label">Your Details</span>
            </div>
            <div class="vc-step-body">
              <div class="vc-grid-2">
                <div class="vc-field">
                  <label class="vc-label">First Name <span class="req">*</span></label>
                  <input v-model="form.first_name" class="vc-input" placeholder="John" />
                </div>
                <div class="vc-field">
                  <label class="vc-label">Last Name <span class="req">*</span></label>
                  <input v-model="form.last_name" class="vc-input" placeholder="Doe" />
                </div>
              </div>
              <div class="vc-grid-2">
                <div class="vc-field">
                  <label class="vc-label">Email</label>
                  <input v-model="form.email" type="email" class="vc-input" placeholder="you@example.com" />
                </div>
                <div class="vc-field">
                  <label class="vc-label">Phone</label>
                  <input v-model="form.phone" class="vc-input" placeholder="+44 7700 000000" />
                </div>
              </div>
              <div class="vc-field">
                <label class="vc-label">Company / Organisation</label>
                <input v-model="form.company" class="vc-input" placeholder="Where are you from? (optional)" />
              </div>
            </div>
          </div>

          <!-- Step 2: Visit Details -->
          <div class="vc-step">
            <div class="vc-step-heading">
              <span class="vc-step-num">2</span>
              <span class="vc-step-label">Visit Details</span>
            </div>
            <div class="vc-step-body">
              <div class="vc-field" style="position:relative;">
                <label class="vc-label">Who are you here to see? <span class="req">*</span></label>
                <div class="vc-search-wrap" :class="{ 'vc-search-wrap--active': form.host_employee_id }">
                  <svg class="vc-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                  <input
                    v-model="hostSearchText"
                    class="vc-input vc-input--search"
                    :class="{ 'vc-input--selected': form.host_employee_id }"
                    placeholder="Search by name, role or department…"
                    autocomplete="off"
                    @input="onHostSearchInput"
                    @focus="hostOpen = true"
                    @blur="onHostBlur"
                  />
                  <button v-if="hostSearchText" class="vc-search-clear" @mousedown.prevent="clearHost" title="Clear">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
                  </button>
                </div>
                <div v-if="hostOpen && (hostResults.length || hostSearching)" class="vc-dropdown">
                  <div v-if="hostSearching" class="vc-dropdown-state">
                    <div class="vc-dropdown-spinner"></div> Searching…
                  </div>
                  <div v-for="h in hostResults" :key="h.id" class="vc-dropdown-option" @mousedown.prevent="pickHost(h)">
                    <div class="vc-opt-avatar">{{ h.full_name[0] }}</div>
                    <div class="vc-opt-info">
                      <div class="vc-opt-name">{{ h.full_name }}</div>
                      <div class="vc-opt-code">{{ h.employee_no }}</div>
                    </div>
                    <svg class="vc-opt-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                  </div>
                </div>
                <div v-else-if="hostOpen && hostSearchText.length >= 1 && !hostSearching" class="vc-dropdown">
                  <div class="vc-dropdown-option vc-dropdown-option--freetext" @mousedown.prevent="useFreeTextHost">
                    <div class="vc-opt-avatar vc-opt-avatar--gray">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                    </div>
                    <div class="vc-opt-info">
                      <div class="vc-opt-name">Visit "{{ hostSearchText }}"</div>
                      <div class="vc-opt-code">Use this name as entered</div>
                    </div>
                    <svg class="vc-opt-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                  </div>
                </div>
              </div>

              <div class="vc-field">
                <label class="vc-label">Purpose of Visit <span class="req">*</span></label>
                <input v-model="form.purpose" class="vc-input" placeholder="e.g. Product demo, Interview, Delivery…" maxlength="300" />
              </div>
            </div>
          </div>

          <!-- Step 3: Photo -->
          <div class="vc-step">
            <div class="vc-step-heading">
              <span class="vc-step-num">3</span>
              <span class="vc-step-label">
                Your Photo
                <span v-if="settings.require_photo" class="req">*</span>
                <span v-else class="vc-step-optional">optional</span>
              </span>
            </div>
            <div class="vc-step-body">
              <!-- Photo consent (separate from NDA) -->
              <div v-if="settings.require_photo && !form.photo_consent" class="vc-photo-consent">
                <div class="vc-photo-consent-icon">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                </div>
                <p class="vc-photo-consent-text">
                  A photo of you will be taken and stored with your visitor record for identification and security purposes.
                  It will be retained for <strong>{{ settings.photo_retention_label || 'the duration set by this organisation' }}</strong>
                  in accordance with their privacy policy.
                </p>
                <label class="vc-nda-accept">
                  <input type="checkbox" v-model="form.photo_consent" class="vc-nda-checkbox" />
                  <span>I consent to my photo being captured and stored</span>
                </label>
              </div>

              <div v-if="!form.photo">
                <div v-if="!camera.active" class="vc-photo-idle">
                  <div class="vc-photo-icon-wrap">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#c8d4e8" stroke-width="1.5"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                  </div>
                  <p class="vc-photo-hint">A photo helps staff recognise you on site.</p>
                  <button class="vc-btn-camera" :disabled="settings.require_photo && !form.photo_consent" @click="openCamera">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                    Open Camera
                  </button>
                  <label class="vc-btn-upload-photo">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    Upload Photo
                    <input type="file" accept="image/*" capture="user" style="display:none;" :disabled="settings.require_photo && !form.photo_consent" @change="handlePhotoFile" />
                  </label>
                </div>
                <div v-else class="vc-camera-active">
                  <video ref="videoEl" autoplay playsinline :class="['vc-camera-video', camera.facing === 'user' && 'vc-mirror']"></video>
                  <p v-if="camera.error" class="vc-camera-error">{{ camera.error }}</p>
                  <div class="vc-camera-controls">
                    <button class="vc-btn-capture" @click="capturePhoto">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
                      Take Photo
                    </button>
                    <button class="vc-btn-switch-cam" @click="switchCamera" title="Flip camera">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 4v6h6"/><path d="M23 20v-6h-6"/><path d="M20.49 9A9 9 0 005.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 013.51 15"/></svg>
                    </button>
                    <button class="vc-btn-cancel-cam" @click="stopCamera">Cancel</button>
                  </div>
                </div>
              </div>
              <div v-else class="vc-photo-preview">
                <img :src="form.photo" class="vc-preview-img" alt="Your photo" />
                <div class="vc-preview-info">
                  <div class="vc-preview-check">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                    Photo captured
                  </div>
                  <button class="vc-btn-retake" @click="retakePhoto">Retake</button>
                </div>
              </div>
              <canvas ref="canvas" style="display:none;"></canvas>
            </div>
          </div>

          <!-- Step 4: ID Document (optional) -->
          <div class="vc-step">
            <div class="vc-step-heading">
              <span class="vc-step-num">4</span>
              <span class="vc-step-label">ID Document <span class="vc-step-optional">optional</span></span>
            </div>
            <div class="vc-step-body">
              <div v-if="!form.id_document" class="vc-photo-idle">
                <div class="vc-photo-icon-wrap">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c8d4e8" stroke-width="1.5"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                </div>
                <p class="vc-photo-hint">Capture your ID card or passport if required by the site.</p>
                <label class="vc-btn-camera" style="cursor:pointer;">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>
                  Capture ID Document
                  <input type="file" accept="image/*" capture="environment" style="display:none;" @change="handleIdDocFile" />
                </label>
              </div>
              <div v-else class="vc-photo-preview">
                <img :src="form.id_document" class="vc-preview-img" alt="ID document" style="width:120px;height:80px;object-fit:cover;" />
                <div class="vc-preview-info">
                  <div class="vc-preview-check">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5"><path d="M20 6L9 17l-5-5"/></svg>
                    ID captured
                  </div>
                  <label class="vc-btn-retake" style="cursor:pointer;">
                    Retake
                    <input type="file" accept="image/*" capture="environment" style="display:none;" @change="handleIdDocFile" />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <!-- Permit to Work (shown when ref visit type requires it) -->
          <div v-if="refVisit && refVisit.visitor_type?.requires_permit" class="vc-step">
            <div class="vc-step-heading">
              <span class="vc-step-num" style="background:#c2410c;">PTW</span>
              <span class="vc-step-label">Permit to Work <span class="req">*</span></span>
            </div>
            <div class="vc-step-body">
              <div class="vc-grid-2">
                <div class="vc-field">
                  <label class="vc-label">Permit Number <span class="req">*</span></label>
                  <input v-model="form.permit_number" class="vc-input" placeholder="e.g. PTW-2026-00142" />
                </div>
                <div class="vc-field">
                  <label class="vc-label">Work Type</label>
                  <select v-model="form.permit_work_type" class="vc-input">
                    <option value="">— Select —</option>
                    <option value="hot_work">Hot Work</option>
                    <option value="electrical">Electrical</option>
                    <option value="mechanical">Mechanical</option>
                    <option value="confined_space">Confined Space</option>
                    <option value="working_at_height">Working at Height</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>
              <div class="vc-grid-2">
                <div class="vc-field">
                  <label class="vc-label">Valid From</label>
                  <input v-model="form.permit_valid_from" type="date" class="vc-input" />
                </div>
                <div class="vc-field">
                  <label class="vc-label">Valid To</label>
                  <input v-model="form.permit_valid_to" type="date" class="vc-input" />
                </div>
              </div>
            </div>
          </div>

          <!-- Step 5: NDA (conditional) -->
          <div v-if="settings.nda_enabled" class="vc-step">
            <div class="vc-step-heading">
              <span class="vc-step-num">5</span>
              <span class="vc-step-label">Visitor Agreement <span class="req">*</span></span>
            </div>
            <div class="vc-step-body">
              <div class="vc-nda-box">{{ settings.nda_text }}</div>
              <label class="vc-nda-accept">
                <input type="checkbox" v-model="form.nda_accepted" class="vc-nda-checkbox" />
                <span>I have read and agree to the visitor agreement above</span>
              </label>
            </div>
          </div>

          <p class="vc-form-footer">Powered by <strong>Mapiibs</strong> · Visitor Management</p>

          <!-- Error + Submit (sticky on mobile) -->
          <div class="vc-submit-dock">
            <div class="vc-submit-area">
              <div v-if="errorMsg" class="vc-error-banner">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {{ errorMsg }}
              </div>
              <button class="vc-btn-submit" @click="submit" :disabled="saving">
                <span v-if="saving" class="vc-submit-spinner"></span>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12l5 5L20 7"/></svg>
                {{ saving ? 'Checking in…' : 'Check In Now' }}
              </button>
            </div>
          </div>
        </div>
      </main>

    </div>

    <!-- ── QR Scanner overlay ──────────────────────────────────────────────── -->
    <div v-if="qrScanner.active" class="vc-qr-overlay" @click.self="stopQrScan">
      <div class="vc-qr-modal">
        <div class="vc-qr-header">
          <span class="vc-qr-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><path d="M14 14h3M17 14v3M14 17h3M20 17v3M17 20h3"/></svg>
            {{ qrScanner.mode === 'checkout' ? 'Scan Your Badge' : 'Scan QR Code' }}
          </span>
          <button class="vc-qr-close" @click="stopQrScan">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div id="vc-qr-reader" class="vc-qr-reader-box"></div>
        <p class="vc-qr-hint">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          {{ qrScanner.mode === 'checkout' ? 'Point the camera at the QR code on your badge' : 'Point the camera at your invitation QR code' }}
        </p>
        <button class="vc-qr-manual-btn" @click="stopQrScan">Enter manually instead</button>
      </div>
    </div>

    <!-- ── Consent re-prompt modal ────────────────────────────────────────── -->
    <div v-if="consentModal.visible" class="vc-consent-overlay" @click.self="consentModal.visible = false">
      <div class="vc-consent-modal">
        <div class="vc-consent-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
        </div>
        <h3>Welcome back, {{ consentModal.visitorName }}</h3>
        <p class="vc-consent-sub">We have a record of your previous visit{{ consentModal.lastVisit ? ' on ' + consentModal.lastVisit : '' }}. Your details are kept on file for a faster check-in.</p>
        <div class="vc-consent-notice">
          <strong>Data we hold:</strong> name, email, phone, and visit history.
          This information is used only to manage your visits and will be kept for up to 12 months.
        </div>
        <label class="vc-consent-agree">
          <input type="checkbox" v-model="consentModal.accepted" />
          <span>I consent to my details being used to process my check-in</span>
        </label>
        <div class="vc-consent-actions">
          <button class="vc-ref-btn" :disabled="!consentModal.accepted || saving" @click="acceptConsent">
            <span v-if="saving" class="vc-submit-spinner" style="width:16px;height:16px;border-width:2px;border-top-color:#fff;"></span>
            {{ saving ? 'Checking in…' : 'Continue & Check In' }}
          </button>
          <button class="vc-ref-back" @click="consentModal.visible = false">Cancel</button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode'

let _qrInstance = null

export default {
  name: 'PublicVisitorCheckIn',

  data() {
    return {
      companyCode:  '',
      businessId:   '',
      company:     { name: '', logo: null },
      loadingCompany: true,
      companyError:   false,
      kioskDisabled:  false,
      showWelcome:    true,

      settings: {
        nda_enabled: false, nda_text: '', require_photo: false, kiosk_enabled: true,
        kiosk_brand_colour: '#004AAD', kiosk_language: 'en',
        privacy_notice_enabled: false, privacy_notice_text: '',
      },

      form: {
        first_name: '', last_name: '', email: '', phone: '',
        company: '', host_employee_id: '', host_name: '', purpose: '', photo: '',
        nda_accepted: false, id_document: null, consent_given: false,
        photo_consent: false,
        permit_number: '', permit_work_type: '', permit_valid_from: '', permit_valid_to: '',
      },

      consentModal: { visible: false, visitorName: '', lastVisit: '', accepted: false },

      hostSearchText: '',
      hostResults:    [],
      hostSearching:  false,
      hostOpen:       false,
      hostSearchTimer: null,

      camera: { active: false, stream: null, error: null, facing: 'user' },
      qrScanner: { active: false, mode: null },

      saving:   false,
      errorMsg: '',
      pass:     null,

      checkingOut:      false,
      checkedOut:       null,
      showCheckOutFlow: false,
      checkOutBadge:    '',
      checkOutError:    '',

      showRefFlow:   false,
      refToken:      '',
      refLooking:    false,
      refVisit:      null,
      refActivating: false,
      refError:      '',
    }
  },

  computed: {
    passInitials() {
      const v = this.pass?.visitor
      return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase()
    },
    refVisitInitials() {
      if (!this.refVisit?.visitor_name) return '?'
      const parts = this.refVisit.visitor_name.trim().split(' ')
      return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase()
    },
    checkoutInitials() {
      const v = this.checkedOut?.visitor
      return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase()
    },
  },

  created() {
    this.companyCode = this.$route.params.companyCode
    this.businessId  = this.$route.params.businessId
    const tokenFromUrl = this.$route.query.token
    this.loadCompany().then(() => {
      if (tokenFromUrl) {
        this.refToken = tokenFromUrl
        this.showWelcome = false
        this.showRefFlow = true
        this.lookupRef()
      }
    })
  },

  beforeUnmount() {
    this.stopCamera()
    this.stopQrScan()
  },

  methods: {
    async loadCompany() {
      try {
        const r = await axios.get(`public/visitors/${this.companyCode}/${this.businessId}/info`)
        const d = r.data.data ?? {}
        this.company  = d
        this.settings = {
          nda_enabled:             d.nda_enabled              ?? false,
          nda_text:                d.nda_text                 ?? '',
          require_photo:           d.require_photo            ?? false,
          kiosk_enabled:           d.kiosk_enabled            ?? true,
          kiosk_brand_colour:      d.kiosk_brand_colour       ?? '#004AAD',
          kiosk_language:          d.kiosk_language           ?? 'en',
          privacy_notice_enabled:  d.privacy_notice_enabled   ?? false,
          privacy_notice_text:     d.privacy_notice_text      ?? '',
        }
        if (!this.settings.kiosk_enabled) {
          this.kioskDisabled = true
        }
      } catch {
        this.companyError = true
      } finally {
        this.loadingCompany = false
      }
    },

    startRefFlow() {
      this.showWelcome = false
      this.showRefFlow = true
    },

    backToWelcome() {
      this.showRefFlow = false
      this.showCheckOutFlow = false
      this.showWelcome = true
      this.refToken = ''
      this.refVisit = null
      this.refError = ''
      this.checkOutBadge = ''
      this.checkOutError = ''
    },

    startCheckOutFlow() {
      this.showWelcome = false
      this.showCheckOutFlow = true
    },

    async checkOutFromPass() {
      this.checkingOut = true
      try {
        const identifier = this.pass.badge_no
          ? { badge_no: this.pass.badge_no }
          : { qr_token: this.pass.qr_token }
        const r = await axios.post(`public/visitors/${this.companyCode}/${this.businessId}/check-out`, identifier)
        this.checkedOut = r.data.data?.visit ?? r.data.data
        this.pass = null
      } catch (e) {
        alert(e.response?.data?.message ?? 'Check-out failed. Please ask reception for help.')
      } finally {
        this.checkingOut = false
      }
    },

    async submitCheckOut() {
      if (!this.checkOutBadge.trim()) return
      this.checkingOut = true
      this.checkOutError = ''
      try {
        const r = await axios.post(`public/visitors/${this.companyCode}/${this.businessId}/check-out`, { badge_no: this.checkOutBadge.trim().toUpperCase() })
        this.checkedOut = r.data.data?.visit ?? r.data.data
        this.showCheckOutFlow = false
      } catch (e) {
        this.checkOutError = e.response?.data?.message ?? 'Check-out failed. Please check your badge number and try again.'
      } finally {
        this.checkingOut = false
      }
    },

    async lookupRef() {
      if (!this.refToken.trim()) return
      this.refLooking = true
      this.refError = ''
      try {
        const r = await axios.get(`public/visitors/${this.companyCode}/${this.businessId}/lookup-prereg`, { params: { token: this.refToken.trim() } })
        this.refVisit = r.data.data?.visit
      } catch (e) {
        this.refError = e.response?.data?.message ?? 'Reference code not found. Please check and try again.'
        this.refVisit = null
      } finally { this.refLooking = false }
    },

    async activateRef() {
      this.refActivating = true
      this.refError = ''
      try {
        const r = await axios.post(`public/visitors/${this.companyCode}/${this.businessId}/activate-prereg`, { token: this.refToken.trim() })
        this.pass = r.data.data?.visit ?? r.data.data
        this.showRefFlow = false
      } catch (e) {
        this.refError = e.response?.data?.message ?? 'Activation failed. Please try again or ask reception for help.'
      } finally { this.refActivating = false }
    },

    onHostSearchInput() {
      clearTimeout(this.hostSearchTimer)
      const q = this.hostSearchText.trim()
      if (q.length < 2) { this.hostResults = []; return }
      this.hostSearchTimer = setTimeout(() => this.fetchHosts(q), 280)
    },

    async fetchHosts(q) {
      this.hostSearching = true
      try {
        const r = await axios.get(`public/visitors/${this.companyCode}/${this.businessId}/hosts`, { params: { search: q } })
        this.hostResults = r.data.data ?? []
      } catch { this.hostResults = [] }
      finally { this.hostSearching = false }
    },

    pickHost(h) {
      this.form.host_employee_id = h.id
      this.form.host_name = ''
      this.hostSearchText = h.full_name
      this.hostResults = []
      this.hostOpen = false
    },

    useFreeTextHost() {
      this.form.host_employee_id = ''
      this.form.host_name = this.hostSearchText.trim()
      this.hostResults = []
      this.hostOpen = false
    },

    clearHost() {
      this.form.host_employee_id = ''
      this.form.host_name = ''
      this.hostSearchText = ''
      this.hostResults = []
    },

    onHostBlur() {
      setTimeout(() => { this.hostOpen = false }, 150)
    },

    async openCamera() {
      this.camera.error = null
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: this.camera.facing, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        })
        this.camera.stream = stream
        this.camera.active = true
        await this.$nextTick()
        this.$refs.videoEl.srcObject = stream
      } catch {
        this.camera.error = 'Camera access denied. Please allow camera access and try again.'
      }
    },

    async switchCamera() {
      this.stopCamera()
      this.camera.facing = this.camera.facing === 'user' ? 'environment' : 'user'
      await this.openCamera()
    },

    capturePhoto() {
      const video  = this.$refs.videoEl
      const canvas = this.$refs.canvas
      canvas.width  = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext('2d')
      if (this.camera.facing === 'user') {
        ctx.translate(canvas.width, 0)
        ctx.scale(-1, 1)
      }
      ctx.drawImage(video, 0, 0)
      this.form.photo = canvas.toDataURL('image/jpeg', 0.85)
      this.stopCamera()
    },

    retakePhoto() {
      this.form.photo = ''
      this.openCamera()
    },

    stopCamera() {
      if (this.camera.stream) {
        this.camera.stream.getTracks().forEach(t => t.stop())
        this.camera.stream = null
      }
      this.camera.active = false
    },

    handlePhotoFile(e) {
      const file = e.target.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = ev => { this.form.photo = ev.target.result }
      reader.readAsDataURL(file)
      e.target.value = ''
    },

    async startQrScan(mode) {
      if (!window.isSecureContext) {
        alert('QR scanning requires a secure connection (HTTPS). Please enter the code manually.')
        return
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        alert('Camera not available on this device. Please enter the code manually.')
        return
      }
      this.qrScanner = { active: true, mode }
      await this.$nextTick()
      try {
        _qrInstance = new Html5Qrcode('vc-qr-reader', {
          formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        })
        await _qrInstance.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (text) => { this.onQrScanned(text, mode); this.stopQrScan() },
          () => {}
        )
      } catch (err) {
        this.qrScanner.active = false
        _qrInstance = null
        const msg = (err?.message ?? '').toLowerCase()
        if (msg.includes('permission') || msg.includes('denied')) {
          alert('Camera permission denied. Please allow camera access and try again.')
        } else if (msg.includes('notfound') || msg.includes('no camera')) {
          alert('No camera found on this device. Please enter the code manually.')
        } else {
          alert('Could not start QR scanner. Please enter the code manually.')
        }
      }
    },

    async stopQrScan() {
      if (_qrInstance) {
        try { await _qrInstance.stop() } catch { /* ignore */ }
        _qrInstance = null
      }
      this.qrScanner.active = false
    },

    onQrScanned(text, mode) {
      if (mode === 'ref') {
        this.refToken = text.trim()
        this.lookupRef()
      } else if (mode === 'checkout') {
        this.checkOutBadge = text.trim().toUpperCase()
        this.submitCheckOut()
      }
    },

    async submit() {
      this.errorMsg = ''
      if (!this.form.first_name.trim() || !this.form.last_name.trim()) {
        this.errorMsg = 'Please enter your first and last name.'
        return
      }
      if (!this.hostSearchText.trim()) {
        this.errorMsg = 'Please enter or search for the person you are visiting.'
        return
      }
      if (!this.form.purpose.trim()) {
        this.errorMsg = 'Please state the purpose of your visit.'
        return
      }
      if (this.settings.require_photo && !this.form.photo) {
        this.errorMsg = 'A photo is required for check-in. Please use the camera above.'
        return
      }
      if (this.settings.require_photo && !this.form.photo_consent) {
        this.errorMsg = 'Please consent to having your photo taken before continuing.'
        return
      }
      if (this.settings.nda_enabled && !this.form.nda_accepted) {
        this.errorMsg = 'You must accept the visitor agreement before checking in.'
        return
      }
      if (this.refVisit?.visitor_type?.requires_permit && !this.form.permit_number.trim()) {
        this.errorMsg = 'A permit to work number is required for this visitor type.'
        return
      }

      // If no employee was picked from the list, carry the typed text as host_name
      if (!this.form.host_employee_id) {
        this.form.host_name = this.hostSearchText.trim()
      }

      this.saving = true
      try {
        const r = await axios.post(`public/visitors/${this.companyCode}/${this.businessId}/check-in`, this.form)
        this.pass = r.data.data?.visit ?? r.data.data
      } catch (e) {
        const data = e.response?.data
        if (e.response?.status === 409 && data?.status === 'consent_required') {
          this.consentModal = {
            visible:      true,
            visitorName:  data.visitor_name ?? '',
            lastVisit:    data.last_visit ? new Date(data.last_visit).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '',
            accepted:     false,
          }
        } else {
          this.errorMsg = data?.message ?? 'Check-in failed. Please try again or ask reception for help.'
        }
      } finally {
        this.saving = false
      }
    },

    async acceptConsent() {
      this.consentModal.visible = false
      this.form.consent_given = true
      await this.submit()
    },

    handleIdDocFile(e) {
      const file = e.target.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = ev => { this.form.id_document = ev.target.result }
      reader.readAsDataURL(file)
    },

    printPass() {
      const el = this.$refs.passCard
      if (!el) return
      const win = window.open('', '_blank', 'width=620,height=900')
      win.document.write(`<!DOCTYPE html><html><head><title>Visitor Pass</title><style>
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:#f4f7fb;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .vc-badge{width:480px;background:#fff;border-radius:20px;box-shadow:0 24px 64px rgba(0,0,0,.18);display:flex;overflow:hidden;}
        .vc-badge-strip{width:8px;background:linear-gradient(180deg,#004AAD 0%,#1565d8 100%);flex-shrink:0;}
        .vc-badge-content{flex:1;padding:24px 24px 20px;}
        .vc-badge-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
        .vc-badge-company{display:flex;align-items:center;gap:8px;}
        .vc-badge-logo{height:24px;width:auto;object-fit:contain;background:#fff;border-radius:4px;padding:2px;}
        .vc-badge-company-name{font-size:13px;font-weight:700;color:#1a2b45;}
        .vc-badge-type-label{font-size:9px;font-weight:800;letter-spacing:.14em;color:#fff;background:#004AAD;padding:3px 9px;border-radius:4px;}
        .vc-badge-identity{display:flex;align-items:center;gap:16px;margin-bottom:20px;}
        .vc-badge-photo-wrap{flex-shrink:0;}
        .vc-badge-photo{width:72px;height:72px;border-radius:12px;object-fit:cover;border:2px solid #e0e7ff;flex-shrink:0;}
        .vc-badge-initials{width:72px;height:72px;border-radius:12px;background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%);color:#fff;font-size:24px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .vc-badge-name-block{min-width:0;}
        .vc-badge-name{font-size:20px;font-weight:800;color:#1a2b45;line-height:1.2;margin-bottom:2px;}
        .vc-badge-org{font-size:12px;color:#7a90b0;margin-bottom:1px;}
        .vc-badge-email{font-size:11px;color:#aab8cc;}
        .vc-badge-no-strip{display:flex;align-items:center;gap:10px;background:#f4f7fb;border-radius:8px;padding:9px 14px;margin-bottom:16px;}
        .vc-badge-barcode-icon{color:#c0cad6;flex-shrink:0;}
        .vc-badge-no{font-size:16px;font-weight:800;color:#1a2b45;letter-spacing:.08em;}
        .vc-badge-details{display:flex;flex-direction:column;border-top:1px solid #f0f4f8;margin-bottom:16px;}
        .vc-badge-detail{display:flex;justify-content:space-between;align-items:baseline;padding:9px 0;border-bottom:1px solid #f0f4f8;}
        .vc-badge-detail:last-child{border-bottom:none;}
        .vc-bd-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#aab8cc;}
        .vc-bd-value{font-size:13px;font-weight:600;color:#1a2b45;text-align:right;max-width:65%;}
        .vc-badge-instruction{font-size:11px;color:#c0cad6;text-align:center;line-height:1.5;padding-top:12px;border-top:1px solid #f0f4f8;}
        @media print{body{background:#fff;min-height:auto;}.vc-badge{box-shadow:none;border:1px solid #e4eaf2;}}
      </style></head><body>${el.outerHTML}</body></html>`)
      win.document.close()
      win.focus()
      setTimeout(() => { win.print(); win.close() }, 300)
    },

    startOver() {
      this.pass = null
      this.checkedOut = null
      this.showWelcome = true
      this.showRefFlow = false
      this.showCheckOutFlow = false
      this.refToken = ''
      this.refVisit = null
      this.refError = ''
      this.checkOutBadge = ''
      this.checkOutError = ''
      this.form = { first_name: '', last_name: '', email: '', phone: '', company: '', host_employee_id: '', host_name: '', purpose: '', photo: '', nda_accepted: false, id_document: null, consent_given: false, photo_consent: false, permit_number: '', permit_work_type: '', permit_valid_from: '', permit_valid_to: '' }
      this.hostSearchText = ''
      this.errorMsg = ''
    },

    hostName(h) { return h ? `${h.first_name ?? ''} ${h.last_name ?? ''}`.trim() : '—' },

    fmtDatetime(v) {
      if (!v) return '—'
      return new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    },
  },
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.vc-root {
  min-height: 100vh;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
  color: #1a2b45;
  background: #f0f4f8;
}

/* ── Splash / Loading ─────────────────────────────────────────────────────── */
.vc-splash {
  min-height: 100vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: 16px; color: #7a90b0;
}
.vc-splash-ring {
  width: 44px; height: 44px;
  border: 3px solid #e4eaf2; border-top-color: #004AAD;
  border-radius: 50%; animation: vc-spin .85s linear infinite;
}
@keyframes vc-spin { to { transform: rotate(360deg); } }

/* ── Error ───────────────────────────────────────────────────────────────── */
.vc-error-screen {
  min-height: 100vh; display: flex; align-items: center;
  justify-content: center; padding: 32px;
}
.vc-error-box {
  background: #fff; border-radius: 16px; padding: 40px 36px;
  text-align: center; max-width: 380px;
  box-shadow: 0 4px 24px rgba(0,0,0,.06);
}
.vc-error-icon {
  width: 60px; height: 60px; border-radius: 50%;
  background: #fef2f2; color: #dc2626;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
}
.vc-error-box h2 { font-size: 18px; font-weight: 700; margin-bottom: 10px; }
.vc-error-box p  { font-size: 14px; color: #7a90b0; line-height: 1.6; }

/* ── Split layout ─────────────────────────────────────────────────────────── */
.vc-layout { display: flex; min-height: 100vh; }

/* Left hero panel */
.vc-hero {
  width: 380px; flex-shrink: 0;
  background: linear-gradient(160deg, #004AAD 0%, #0a2d6e 100%);
  padding: 48px 40px;
  display: flex; flex-direction: column;
  position: sticky; top: 0; height: 100vh;
  overflow: hidden;
}
.vc-hero-inner { position: relative; z-index: 1; display: flex; flex-direction: column; height: 100%; }

.vc-hero-brand { display: flex; align-items: center; gap: 12px; margin-bottom: auto; }
.vc-hero-logo-wrap {
  width: 56px; height: 56px; border-radius: 12px;
  background: #fff; display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; box-shadow: 0 4px 16px rgba(0,0,0,.18);
  padding: 6px;
}
.vc-hero-logo { width: 100%; height: 100%; object-fit: contain; }
.vc-hero-logo-placeholder { display: flex; align-items: center; justify-content: center; }
.vc-hero-company-name { font-size: 15px; font-weight: 700; color: rgba(255,255,255,.9); }

.vc-hero-text { margin: auto 0 32px; }
.vc-hero-heading {
  font-size: 42px; font-weight: 800; line-height: 1.1;
  color: #fff; margin-bottom: 16px; letter-spacing: -.02em;
}
.vc-hero-desc { font-size: 14px; color: rgba(255,255,255,.65); line-height: 1.7; max-width: 260px; }

.vc-hero-features { list-style: none; display: flex; flex-direction: column; gap: 12px; }
.vc-hero-features li {
  display: flex; align-items: center; gap: 10px;
  font-size: 13px; color: rgba(255,255,255,.75);
}
.vc-feat-icon {
  width: 22px; height: 22px; border-radius: 50%;
  background: rgba(255,255,255,.15); color: #fff;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}

/* Decorative circles */
.vc-deco-circle {
  position: absolute; border-radius: 50%;
  background: rgba(255,255,255,.04);
}
.vc-deco-1 { width: 300px; height: 300px; bottom: -80px; right: -80px; }
.vc-deco-2 { width: 160px; height: 160px; top: 60px; right: 20px; background: rgba(255,255,255,.06); }

/* Right form panel */
.vc-form-panel {
  flex: 1; overflow-y: auto;
  background: #f0f4f8;
  display: flex; flex-direction: column;
}
.vc-form-scroll {
  max-width: 560px; width: 100%;
  margin: 0 auto; padding: 48px 32px 40px;
  flex: 1;
}

.vc-form-header { margin-bottom: 32px; }
.vc-form-title { font-size: 26px; font-weight: 800; color: #1a2b45; margin-bottom: 6px; letter-spacing: -.02em; }
.vc-form-sub { font-size: 13px; color: #7a90b0; }
.req { color: #dc2626; }

/* ── Steps ───────────────────────────────────────────────────────────────── */
.vc-step {
  background: #fff; border-radius: 14px; overflow: hidden;
  box-shadow: 0 1px 4px rgba(0,0,0,.05); margin-bottom: 16px;
}
.vc-step-heading {
  display: flex; align-items: center; gap: 12px;
  padding: 18px 22px; border-bottom: 1px solid #f0f4f8;
}
.vc-step-num {
  width: 28px; height: 28px; border-radius: 50%;
  background: #004AAD; color: #fff;
  font-size: 12px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.vc-step-label { font-size: 14px; font-weight: 700; color: #1a2b45; }
.vc-step-optional { font-size: 11px; font-weight: 400; color: #aab8cc; margin-left: 6px; }
.vc-nda-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; padding:14px 16px; font-size:12.5px; color:#4a5e78; line-height:1.6; max-height:180px; overflow-y:auto; white-space:pre-wrap; }
.vc-nda-accept { display:flex; align-items:flex-start; gap:10px; font-size:13px; color:#1a2b45; cursor:pointer; margin-top:2px; }
.vc-nda-checkbox { margin-top:2px; width:16px; height:16px; flex-shrink:0; accent-color:#004AAD; cursor:pointer; }
.vc-step-body { padding: 22px; display: flex; flex-direction: column; gap: 16px; }

/* ── Form fields ─────────────────────────────────────────────────────────── */
.vc-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.vc-field  { display: flex; flex-direction: column; gap: 6px; }
.vc-label  { font-size: 12px; font-weight: 600; color: #4a5e78; }
.vc-input  {
  width: 100%; height: 44px; padding: 0 14px;
  border: 1.5px solid #e4eaf2; border-radius: 9px;
  font-size: 14px; color: #1a2b45; background: #fff;
  outline: none; font-family: inherit;
  transition: border-color .15s, box-shadow .15s;
}
.vc-input:focus {
  border-color: #004AAD;
  box-shadow: 0 0 0 3px rgba(0,74,173,.1);
}
.vc-input::placeholder { color: #c0cad6; }

/* ── Host search ─────────────────────────────────────────────────────────── */
.vc-search-wrap { position: relative; }
.vc-search-icon {
  position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
  color: #aab8cc; pointer-events: none;
}
.vc-input--search { padding-left: 38px; padding-right: 36px; }
.vc-input--selected { border-color: #004AAD; background: #f0f6ff; color: #004AAD; font-weight: 600; }
.vc-search-clear {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  background: none; border: none; cursor: pointer; color: #aab8cc; padding: 4px;
  display: flex; align-items: center;
}
.vc-search-clear:hover { color: #dc2626; }

.vc-dropdown {
  position: absolute; left: 0; right: 0; top: calc(100% + 6px); z-index: 100;
  background: #fff; border: 1.5px solid #e4eaf2; border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0,0,0,.12); overflow: hidden;
  max-height: 260px; overflow-y: auto;
}
.vc-dropdown-state {
  display: flex; align-items: center; gap: 8px;
  padding: 14px 16px; font-size: 13px; color: #7a90b0;
}
.vc-dropdown-empty { flex-direction: column; gap: 6px; padding: 20px; justify-content: center; }
.vc-dropdown-spinner {
  width: 14px; height: 14px; border: 2px solid #e4eaf2; border-top-color: #004AAD;
  border-radius: 50%; animation: vc-spin .7s linear infinite; flex-shrink: 0;
}
.vc-dropdown-option {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 16px; cursor: pointer; transition: background .1s;
}
.vc-dropdown-option:hover { background: #f4f7fb; }
.vc-opt-avatar {
  width: 34px; height: 34px; border-radius: 50%;
  background: #e0e7ff; color: #4338ca;
  font-size: 13px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.vc-opt-info { flex: 1; min-width: 0; }
.vc-opt-name { font-size: 13px; font-weight: 600; color: #1a2b45; }
.vc-opt-code { font-size: 11px; color: #aab8cc; }
.vc-opt-arrow { color: #c0cad6; flex-shrink: 0; }
.vc-dropdown-option--freetext { border-top: 1px solid #f0f4f8; }
.vc-dropdown-option--freetext .vc-opt-arrow { color: #16a34a; }
.vc-opt-avatar--gray { background: #f1f5f9; color: #64748b; }

/* ── Photo consent ───────────────────────────────────────────────────────── */
.vc-photo-consent { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
.vc-photo-consent-icon { color: #2563eb; }
.vc-photo-consent-text { font-size: 12px; color: #1e40af; line-height: 1.6; margin: 0; }

/* ── Photo section ───────────────────────────────────────────────────────── */
.vc-photo-idle {
  background: #f8fafc; border: 1.5px dashed #dde5f0; border-radius: 12px;
  padding: 28px 20px; display: flex; flex-direction: column;
  align-items: center; gap: 10px; text-align: center;
}
.vc-photo-icon-wrap {
  width: 56px; height: 56px; border-radius: 50%;
  background: #eef2f8; display: flex; align-items: center; justify-content: center;
}
.vc-photo-hint { font-size: 13px; color: #aab8cc; }
.vc-btn-camera {
  display: inline-flex; align-items: center; gap: 7px;
  background: #fff; color: #004AAD; border: 1.5px solid #004AAD;
  border-radius: 8px; padding: 8px 18px; font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit; transition: background .15s;
}
.vc-btn-camera:hover { background: #f0f6ff; }

.vc-camera-active { border-radius: 12px; overflow: hidden; background: #0a0a0a; }
.vc-camera-video { width: 100%; max-height: 320px; display: block; object-fit: cover; }
.vc-camera-video.vc-mirror { transform: scaleX(-1); }
.vc-camera-error { font-size: 13px; color: #ef4444; padding: 10px 16px; background: #0a0a0a; text-align: center; }
.vc-camera-controls {
  display: flex; align-items: center; gap: 10px; justify-content: center;
  padding: 14px 16px; background: #111;
}
.vc-btn-capture {
  display: inline-flex; align-items: center; gap: 8px;
  background: #004AAD; color: #fff; border: none; border-radius: 9px;
  padding: 10px 22px; font-size: 13px; font-weight: 700;
  cursor: pointer; font-family: inherit;
}
.vc-btn-capture:hover { background: #003b8e; }
.vc-btn-cancel-cam {
  background: rgba(255,255,255,.08); color: rgba(255,255,255,.7);
  border: 1px solid rgba(255,255,255,.15); border-radius: 9px;
  padding: 10px 18px; font-size: 13px; font-weight: 600;
  cursor: pointer; font-family: inherit;
}

.vc-photo-preview { display: flex; align-items: center; gap: 16px; }
.vc-preview-img {
  width: 80px; height: 80px; border-radius: 12px; object-fit: cover;
  border: 2px solid #e0e7ff;
}
.vc-preview-info { display: flex; flex-direction: column; gap: 8px; }
.vc-preview-check {
  display: flex; align-items: center; gap: 6px;
  font-size: 13px; font-weight: 600; color: #16a34a;
}
.vc-btn-retake {
  display: inline-flex; align-items: center;
  background: #f4f7fb; color: #3d5068; border: 1px solid #e4eaf2;
  border-radius: 7px; padding: 6px 14px; font-size: 12px; font-weight: 600;
  cursor: pointer; font-family: inherit; width: fit-content;
}
.vc-btn-retake:hover { background: #e4eaf2; }

/* ── Submit area ─────────────────────────────────────────────────────────── */
.vc-submit-area { display: flex; flex-direction: column; gap: 12px; margin-top: 8px; }
.vc-error-banner {
  display: flex; align-items: center; gap: 10px;
  background: #fef2f2; color: #dc2626; border: 1px solid #fecaca;
  border-radius: 10px; padding: 12px 16px; font-size: 13px; font-weight: 500;
}
.vc-btn-submit {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; height: 54px; background: #004AAD; color: #fff;
  border: none; border-radius: 12px; font-size: 16px; font-weight: 700;
  cursor: pointer; font-family: inherit; transition: background .15s, transform .1s;
  box-shadow: 0 4px 16px rgba(0,74,173,.3);
}
.vc-btn-submit:hover:not(:disabled) { background: #003b8e; transform: translateY(-1px); box-shadow: 0 6px 20px rgba(0,74,173,.35); }
.vc-btn-submit:active:not(:disabled) { transform: translateY(0); }
.vc-btn-submit:disabled { opacity: .6; cursor: not-allowed; box-shadow: none; }
.vc-submit-spinner {
  width: 18px; height: 18px; border: 2px solid rgba(255,255,255,.35);
  border-top-color: #fff; border-radius: 50%;
  animation: vc-spin .75s linear infinite;
}

.vc-form-footer {
  text-align: center; padding-top: 28px;
  font-size: 12px; color: #c0cad6;
}
.vc-form-footer strong { color: #aab8cc; }

/* ── Visitor Pass screen ─────────────────────────────────────────────────── */
.vc-pass-screen {
  min-height: 100vh; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 24px; padding: 40px 20px;
  background: linear-gradient(160deg, #004AAD 0%, #0a2d6e 55%, #f0f4f8 55%);
}

.vc-pass-success-header {
  display: flex; align-items: center; gap: 14px;
  width: 100%; max-width: 480px;
}
.vc-pass-success-icon {
  width: 52px; height: 52px; border-radius: 50%;
  background: rgba(255,255,255,.2); flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
}
.vc-pass-success-title { font-size: 20px; font-weight: 800; color: #fff; margin-bottom: 2px; }
.vc-pass-success-sub   { font-size: 13px; color: rgba(255,255,255,.7); }

/* Badge card */
.vc-badge {
  width: 100%; max-width: 480px;
  background: #fff; border-radius: 20px;
  box-shadow: 0 24px 64px rgba(0,0,0,.18);
  display: flex; overflow: hidden;
}
.vc-badge-strip { width: 8px; background: linear-gradient(180deg, #004AAD 0%, #1565d8 100%); flex-shrink: 0; }
.vc-badge-content { flex: 1; padding: 24px 24px 20px; }

.vc-badge-top {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 20px;
}
.vc-badge-company { display: flex; align-items: center; gap: 8px; }
.vc-badge-logo { height: 24px; width: auto; object-fit: contain; }
.vc-badge-company-name { font-size: 13px; font-weight: 700; color: #1a2b45; }
.vc-badge-type-label {
  font-size: 9px; font-weight: 800; letter-spacing: .14em;
  color: #fff; background: #004AAD; padding: 3px 9px; border-radius: 4px;
}

.vc-badge-identity { display: flex; align-items: center; gap: 16px; margin-bottom: 20px; }
.vc-badge-photo-wrap { flex-shrink: 0; }
.vc-badge-photo {
  width: 72px; height: 72px; border-radius: 12px; object-fit: cover;
  border: 2px solid #e0e7ff;
}
.vc-badge-initials {
  width: 72px; height: 72px; border-radius: 12px;
  background: linear-gradient(135deg, #004AAD 0%, #1565d8 100%);
  color: #fff; font-size: 24px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}
.vc-badge-name-block { min-width: 0; }
.vc-badge-name  { font-size: 20px; font-weight: 800; color: #1a2b45; line-height: 1.2; margin-bottom: 2px; }
.vc-badge-org   { font-size: 12px; color: #7a90b0; margin-bottom: 1px; }
.vc-badge-email { font-size: 11px; color: #aab8cc; }

.vc-badge-no-strip {
  display: flex; align-items: center; gap: 10px;
  background: #f4f7fb; border-radius: 8px;
  padding: 9px 14px; margin-bottom: 16px;
}
.vc-badge-barcode-icon { color: #c0cad6; flex-shrink: 0; }
.vc-badge-no { font-size: 16px; font-weight: 800; color: #1a2b45; letter-spacing: .08em; font-variant-numeric: tabular-nums; }

.vc-badge-details { display: flex; flex-direction: column; gap: 0; border-top: 1px solid #f0f4f8; margin-bottom: 16px; }
.vc-badge-detail { display: flex; justify-content: space-between; align-items: baseline; padding: 9px 0; border-bottom: 1px solid #f0f4f8; }
.vc-badge-detail:last-child { border-bottom: none; }
.vc-bd-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #aab8cc; }
.vc-bd-value { font-size: 13px; font-weight: 600; color: #1a2b45; text-align: right; max-width: 65%; }

.vc-badge-instruction {
  font-size: 11px; color: #c0cad6; text-align: center; line-height: 1.5;
  padding-top: 12px; border-top: 1px solid #f0f4f8;
}

/* Pass action buttons */
.vc-pass-actions { display: flex; gap: 12px; flex-wrap: wrap; justify-content: center; }
.vc-btn-print {
  display: inline-flex; align-items: center; gap: 8px;
  background: #004AAD; color: #fff; border: none; border-radius: 10px;
  padding: 12px 24px; font-size: 14px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 14px rgba(0,74,173,.3);
}
.vc-btn-print:hover { background: #003b8e; }
.vc-btn-checkout {
  display: inline-flex; align-items: center; gap: 8px;
  background: #16a34a; color: #fff; border: none; border-radius: 10px;
  padding: 12px 24px; font-size: 14px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  box-shadow: 0 4px 14px rgba(22,163,74,.3);
}
.vc-btn-checkout:hover:not(:disabled) { background: #15803d; }
.vc-btn-checkout:disabled { opacity: .6; cursor: not-allowed; }
.vc-btn-ghost {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,.9); color: #3d5068;
  border: 1.5px solid #e4eaf2; border-radius: 10px;
  padding: 12px 24px; font-size: 14px; font-weight: 700;
  cursor: pointer; font-family: inherit;
}
.vc-btn-ghost:hover { background: #fff; }

/* ── Welcome screen ─────────────────────────────────────────────────────── */
.vc-welcome {
  min-height: 100vh; position: relative; overflow: hidden;
  background: linear-gradient(145deg, #003a8a 0%, #004AAD 40%, #1a6dd4 100%);
  display: flex; flex-direction: column;
  align-items: center; justify-content: center; padding: 40px 20px;
  gap: 20px;
}
.vc-welcome-bg { position: absolute; inset: 0; pointer-events: none; }
.vc-welcome-deco {
  position: absolute; border-radius: 50%;
  background: rgba(255,255,255,.06);
}
.vc-wd-1 { width: 500px; height: 500px; bottom: -160px; right: -100px; }
.vc-wd-2 { width: 260px; height: 260px; top: -60px; left: -60px; background: rgba(255,255,255,.05); }
.vc-wd-3 { width: 160px; height: 160px; top: 40%; right: 10%; background: rgba(255,255,255,.04); }

.vc-welcome-card {
  position: relative; z-index: 1;
  background: rgba(255,255,255,.08);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255,255,255,.18);
  border-radius: 28px; padding: 48px 44px;
  max-width: 460px; width: 100%;
  text-align: center; display: flex; flex-direction: column; align-items: center; gap: 0;
}

.vc-welcome-logo-wrap { margin-bottom: 28px; }
.vc-welcome-logo {
  width: 96px; height: 96px; border-radius: 22px; object-fit: contain;
  background: rgba(255,255,255,.15); padding: 10px;
  box-shadow: 0 8px 32px rgba(0,0,0,.2);
}
.vc-welcome-logo-fallback {
  width: 96px; height: 96px; border-radius: 22px;
  background: rgba(255,255,255,.2);
  font-size: 40px; font-weight: 800; color: #fff;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 8px 32px rgba(0,0,0,.2);
}

.vc-welcome-text { margin-bottom: 32px; }
.vc-welcome-eyebrow {
  font-size: 12px; font-weight: 600; letter-spacing: .1em;
  text-transform: uppercase; color: rgba(255,255,255,.6); margin-bottom: 8px;
}
.vc-welcome-company {
  font-size: 30px; font-weight: 800; color: #fff; line-height: 1.15;
  margin-bottom: 12px; letter-spacing: -.02em;
}
.vc-welcome-tagline { font-size: 14px; color: rgba(255,255,255,.65); line-height: 1.6; }

.vc-welcome-cta {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; height: 56px; background: #fff; color: #004AAD;
  border: none; border-radius: 14px; font-size: 16px; font-weight: 800;
  cursor: pointer; font-family: inherit; margin-bottom: 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,.2);
  transition: transform .15s, box-shadow .15s;
}
.vc-welcome-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,0,0,.25); }
.vc-welcome-cta:active { transform: translateY(0); }

.vc-welcome-divider {
  width: 100%; display: flex; align-items: center; gap: 12px;
  margin: 4px 0 12px;
}
.vc-welcome-divider::before, .vc-welcome-divider::after {
  content: ''; flex: 1; height: 1px; background: rgba(255,255,255,.2);
}
.vc-welcome-divider span { font-size: 12px; color: rgba(255,255,255,.4); white-space: nowrap; }

.vc-welcome-ref-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; height: 48px;
  background: rgba(255,255,255,.12); border: 1.5px solid rgba(255,255,255,.25);
  border-radius: 12px; color: rgba(255,255,255,.85);
  font-size: 14px; font-weight: 600; cursor: pointer; font-family: inherit;
  margin-bottom: 16px;
  transition: background .15s;
}
.vc-welcome-ref-btn:hover { background: rgba(255,255,255,.2); }

.vc-welcome-note {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 12px; color: rgba(255,255,255,.5);
}

.vc-welcome-powered {
  position: relative; z-index: 1;
  font-size: 12px; color: rgba(255,255,255,.35);
}
.vc-welcome-powered strong { color: rgba(255,255,255,.55); }


/* ── Pre-registration ref flow ───────────────────────────────────────────── */
.vc-ref-screen {
  min-height: 100vh; background: #f0f4f8;
  display: flex; align-items: center; justify-content: center;
  padding: 32px 20px; padding-bottom: 60px;
}
.vc-ref-card {
  background: #fff; border-radius: 20px;
  box-shadow: 0 4px 32px rgba(0,0,0,.09);
  padding: 40px 36px; max-width: 460px; width: 100%;
  text-align: center;
}
.vc-ref-icon-wrap {
  width: 64px; height: 64px; border-radius: 16px;
  background: #e8f0fe; display: flex; align-items: center; justify-content: center;
  margin: 0 auto 20px;
}
.vc-ref-title { font-size: 22px; font-weight: 800; color: #1a2b45; margin-bottom: 10px; }
.vc-ref-sub { font-size: 13px; color: #7a90b0; line-height: 1.6; margin-bottom: 28px; }

.vc-ref-form { display: flex; flex-direction: column; gap: 14px; }
.vc-ref-field { display: flex; flex-direction: column; gap: 5px; text-align: left; }
.vc-ref-label { font-size: 12px; font-weight: 600; color: #4a5e78; }
.vc-ref-input {
  width: 100%; padding: 11px 14px; border: 1.5px solid #dde5f0; border-radius: 10px;
  font-size: 14px; color: #1a2b45; outline: none; box-sizing: border-box; font-family: inherit;
  text-align: center; letter-spacing: .03em;
}
.vc-ref-input:focus { border-color: #004AAD; box-shadow: 0 0 0 3px rgba(0,74,173,.1); }
.vc-ref-error {
  display: flex; align-items: center; gap: 6px;
  background: #fef2f2; color: #dc2626; border-radius: 8px;
  padding: 10px 14px; font-size: 13px; text-align: left;
}
.vc-ref-btn {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  width: 100%; height: 50px; background: #004AAD; color: #fff;
  border: none; border-radius: 12px; font-size: 15px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  transition: background .15s;
}
.vc-ref-btn:hover:not(:disabled) { background: #003b8e; }
.vc-ref-btn:disabled { opacity: .6; cursor: not-allowed; }
.vc-ref-back {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  background: none; border: none; color: #7a90b0; font-size: 13px;
  cursor: pointer; font-family: inherit; padding: 4px 0;
}
.vc-ref-back:hover { color: #004AAD; }

.vc-ref-preview { display: flex; flex-direction: column; align-items: center; gap: 14px; }
.vc-ref-vis-avatar {
  width: 64px; height: 64px; border-radius: 50%;
  background: linear-gradient(135deg, #004AAD 0%, #1565d8 100%);
  color: #fff; font-size: 22px; font-weight: 800;
  display: flex; align-items: center; justify-content: center;
}
.vc-ref-vis-name { font-size: 20px; font-weight: 800; color: #1a2b45; }
.vc-ref-vis-sub { font-size: 13px; color: #7a90b0; margin-top: -6px; }
.vc-ref-details {
  width: 100%; background: #f8fafc; border-radius: 10px;
  padding: 4px 16px; text-align: left;
}
.vc-ref-detail-row {
  display: flex; justify-content: space-between; align-items: baseline;
  padding: 9px 0; border-bottom: 1px solid #f0f4f8; font-size: 13px;
}
.vc-ref-detail-row:last-child { border-bottom: none; }
.vc-ref-detail-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #aab8cc; }
.vc-ref-detail-val { font-weight: 600; color: #1a2b45; max-width: 65%; text-align: right; }

/* ── Brand CSS variable ──────────────────────────────────────────────────── */
.vc-root { --brand: #004AAD; }
.vc-splash-ring           { border-top-color: var(--brand); }
.vc-step-num              { background: var(--brand); }
.vc-input:focus           { border-color: var(--brand); box-shadow: 0 0 0 3px color-mix(in srgb, var(--brand) 15%, transparent); }
.vc-input--selected       { border-color: var(--brand); color: var(--brand); background: color-mix(in srgb, var(--brand) 8%, white); }
.vc-btn-camera            { color: var(--brand); border-color: var(--brand); }
.vc-btn-camera:hover      { background: color-mix(in srgb, var(--brand) 6%, white); }
.vc-btn-capture           { background: var(--brand); }
.vc-btn-capture:hover     { background: var(--brand); filter: brightness(.9); }
.vc-btn-submit            { background: var(--brand); box-shadow: 0 4px 16px color-mix(in srgb, var(--brand) 40%, transparent); }
.vc-btn-submit:hover:not(:disabled) { background: var(--brand); filter: brightness(.9); transform: translateY(-1px); }
.vc-ref-btn               { background: var(--brand); }
.vc-ref-btn:hover:not(:disabled) { background: var(--brand); filter: brightness(.9); }
.vc-btn-print             { background: var(--brand); }
.vc-btn-print:hover       { background: var(--brand); filter: brightness(.9); }
.vc-badge-type-label      { background: var(--brand); }
.vc-badge-strip           { background: linear-gradient(180deg, var(--brand) 0%, color-mix(in srgb, var(--brand) 80%, black) 100%); }
.vc-badge-initials        { background: linear-gradient(135deg, var(--brand) 0%, color-mix(in srgb, var(--brand) 75%, #1565d8) 100%); }
.vc-hero                  { background: linear-gradient(160deg, var(--brand) 0%, color-mix(in srgb, var(--brand) 60%, black) 100%); }
.vc-welcome               { background: linear-gradient(145deg, color-mix(in srgb, var(--brand) 80%, black) 0%, var(--brand) 40%, color-mix(in srgb, var(--brand) 80%, #1a6dd4) 100%); }
.vc-welcome-cta           { color: var(--brand); }
.vc-company               { color: var(--brand); }

/* ── QR Scanner overlay ──────────────────────────────────────────────────── */
.vc-qr-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.75);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000; padding: 20px;
}
.vc-qr-modal {
  background: #fff; border-radius: 20px;
  width: 100%; max-width: 420px;
  box-shadow: 0 24px 64px rgba(0,0,0,.35);
  overflow: hidden;
}
.vc-qr-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 20px; border-bottom: 1px solid #f0f4f8;
}
.vc-qr-title {
  display: flex; align-items: center; gap: 8px;
  font-size: 15px; font-weight: 700; color: #1a2b45;
}
.vc-qr-close {
  width: 32px; height: 32px; border-radius: 50%;
  background: #f4f7fb; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center; color: #4a5e78;
}
.vc-qr-close:hover { background: #e4eaf2; }
.vc-qr-reader-box {
  width: 100%; background: #000;
  /* Html5Qrcode injects its own sizing; container just needs to exist */
}
.vc-qr-hint {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 12px; color: #7a90b0; padding: 12px 20px 4px; text-align: center;
}
.vc-qr-manual-btn {
  display: block; width: 100%; background: none; border: none;
  font-size: 13px; color: #004AAD; font-weight: 600; font-family: inherit;
  cursor: pointer; padding: 12px 20px 18px; text-align: center;
}
.vc-qr-manual-btn:hover { text-decoration: underline; }

/* QR scan trigger button (in ref / checkout flows) */
.vc-qr-trigger {
  display: flex; align-items: center; justify-content: center; gap: 10px;
  width: 100%; height: 50px; background: #fff;
  border: 2px solid var(--brand, #004AAD); border-radius: 12px;
  color: var(--brand, #004AAD); font-size: 15px; font-weight: 700;
  cursor: pointer; font-family: inherit; transition: background .15s;
}
.vc-qr-trigger:hover { background: color-mix(in srgb, var(--brand, #004AAD) 6%, white); }

/* "or" divider between QR trigger and manual input */
.vc-flow-divider {
  display: flex; align-items: center; gap: 10px;
  margin: 4px 0;
}
.vc-flow-divider::before, .vc-flow-divider::after {
  content: ''; flex: 1; height: 1px; background: #e4eaf2;
}
.vc-flow-divider span { font-size: 12px; color: #aab8cc; white-space: nowrap; }

/* Camera switch button */
.vc-btn-switch-cam {
  background: rgba(255,255,255,.1); border: 1px solid rgba(255,255,255,.2);
  border-radius: 9px; padding: 10px 14px; color: rgba(255,255,255,.8);
  display: flex; align-items: center; cursor: pointer; font-family: inherit;
}
.vc-btn-switch-cam:hover { background: rgba(255,255,255,.2); }

/* Upload photo option */
.vc-btn-upload-photo {
  display: inline-flex; align-items: center; gap: 7px;
  background: none; color: #7a90b0; border: 1.5px dashed #c0cad6;
  border-radius: 8px; padding: 8px 18px; font-size: 12px; font-weight: 600;
  cursor: pointer; font-family: inherit; margin-top: 4px;
  transition: border-color .15s, color .15s;
}
.vc-btn-upload-photo:hover { border-color: #004AAD; color: #004AAD; }

/* ── Consent modal ───────────────────────────────────────────────────────── */
.vc-consent-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999; padding: 24px;
}
.vc-consent-modal {
  background: #fff; border-radius: 20px; padding: 36px 32px;
  max-width: 440px; width: 100%;
  box-shadow: 0 24px 64px rgba(0,0,0,.2);
  text-align: center;
}
.vc-consent-icon {
  width: 56px; height: 56px; border-radius: 50%;
  background: #eff6ff; color: var(--brand);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px;
}
.vc-consent-modal h3 { font-size: 18px; font-weight: 800; color: #1a2b45; margin-bottom: 8px; }
.vc-consent-sub { font-size: 13px; color: #7a90b0; line-height: 1.6; margin-bottom: 14px; }
.vc-consent-notice {
  font-size: 12px; color: #4a5e78; line-height: 1.6;
  background: #f8fafc; border: 1px solid #e4eaf2; border-radius: 8px;
  padding: 12px 14px; text-align: left; margin-bottom: 16px;
}
.vc-consent-agree {
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 13px; color: #1a2b45; cursor: pointer;
  text-align: left; margin-bottom: 20px;
}
.vc-consent-agree input[type="checkbox"] { margin-top: 2px; width: 16px; height: 16px; flex-shrink: 0; accent-color: var(--brand); }
.vc-consent-actions { display: flex; flex-direction: column; gap: 10px; }

/* ── Mobile-only compact header (hidden on desktop) ─────────────────────── */
.vc-mobile-header { display: none; }

/* ── Responsive: tablet (≤ 780px) ───────────────────────────────────────── */
@media (max-width: 780px) {
  .vc-layout { flex-direction: column; }
  .vc-hero {
    width: 100%; height: auto; position: static;
    padding: 32px 28px 28px;
  }
  .vc-hero-heading { font-size: 30px; }
  .vc-hero-text { margin: 20px 0; }
  .vc-hero-features { display: none; }
  .vc-deco-1, .vc-deco-2 { display: none; }
  .vc-form-scroll { padding: 28px 20px; }
  .vc-grid-2 { grid-template-columns: 1fr; }
  .vc-pass-screen { background: #f0f4f8; gap: 20px; padding: 28px 16px; }
  .vc-pass-success-header { display: none; }
  .vc-badge-name { font-size: 17px; }
  .vc-welcome-card { padding: 40px 32px; }
  .vc-ref-card { padding: 36px 28px; }
}

/* ── Responsive: phone (≤ 640px) — hero hidden, compact mobile layout ──── */
@media (max-width: 640px) {
  /* Show mobile header, hide hero panel entirely */
  .vc-mobile-header {
    display: flex; align-items: center; justify-content: space-between;
    background: var(--brand, #004AAD); padding: 12px 16px;
    position: sticky; top: 0; z-index: 50;
  }
  .vc-mh-brand { display: flex; align-items: center; gap: 10px; min-width: 0; }
  .vc-mh-logo-wrap {
    width: 32px; height: 32px; border-radius: 8px;
    background: rgba(255,255,255,.2); display: flex;
    align-items: center; justify-content: center; flex-shrink: 0;
    overflow: hidden;
  }
  .vc-mh-logo { width: 100%; height: 100%; object-fit: contain; }
  .vc-mh-logo-fallback {
    font-size: 15px; font-weight: 800; color: #fff;
  }
  .vc-mh-name {
    font-size: 14px; font-weight: 700; color: rgba(255,255,255,.9);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .vc-mh-badge {
    font-size: 11px; font-weight: 700; color: rgba(255,255,255,.7);
    background: rgba(255,255,255,.15); border-radius: 6px;
    padding: 3px 8px; white-space: nowrap; flex-shrink: 0;
  }

  .vc-hero { display: none; }
  .vc-layout { flex-direction: column; min-height: 100vh; }
  .vc-form-panel { min-height: calc(100vh - 56px); }
  .vc-form-scroll { padding: 20px 16px 16px; }

  .vc-form-title { font-size: 20px; }
  .vc-form-header { margin-bottom: 20px; }
  .vc-step-body { padding: 16px; }
  .vc-step-heading { padding: 14px 16px; }

  /* Grid always single column on phones */
  .vc-grid-2 { grid-template-columns: 1fr; }

  /* Sticky submit dock at bottom */
  .vc-submit-dock {
    position: sticky; bottom: 0; left: 0; right: 0;
    background: #f0f4f8;
    padding: 12px 0 max(16px, env(safe-area-inset-bottom));
    border-top: 1px solid #e4eaf2;
    margin-top: 8px;
  }
  .vc-submit-area { gap: 10px; margin-top: 0; }
  .vc-btn-submit { height: 50px; font-size: 15px; border-radius: 12px; }

  /* Pass screen */
  .vc-pass-screen { padding: 16px; gap: 16px; }
  .vc-badge { max-width: 100%; }
  .vc-badge-content { padding: 18px 16px 16px; }
  .vc-badge-name { font-size: 16px; }
  .vc-badge-identity { gap: 12px; }
  .vc-badge-photo, .vc-badge-initials { width: 60px; height: 60px; }

  /* Pass action buttons: stack vertically, full-width */
  .vc-pass-actions { flex-direction: column; width: 100%; align-items: stretch; }
  .vc-btn-print, .vc-btn-checkout, .vc-btn-ghost {
    width: 100%; justify-content: center;
    padding: 14px 20px; font-size: 15px;
  }

  /* Welcome screen */
  .vc-welcome { padding: 28px 16px; }
  .vc-welcome-card { padding: 28px 20px; border-radius: 20px; }
  .vc-welcome-logo { width: 80px; height: 80px; }
  .vc-welcome-logo-fallback { width: 80px; height: 80px; font-size: 32px; }
  .vc-welcome-company { font-size: 22px; }
  .vc-welcome-logo-wrap { margin-bottom: 20px; }
  .vc-welcome-text { margin-bottom: 24px; }

  /* Ref / check-out card */
  .vc-ref-card { padding: 28px 20px; border-radius: 16px; }
  .vc-ref-title { font-size: 19px; }

  /* Consent modal */
  .vc-consent-modal { padding: 28px 20px; border-radius: 16px; }

  /* Dropdown max-height reduction for small screens */
  .vc-dropdown { max-height: 200px; }
}

/* ── Responsive: small phone (≤ 400px) ──────────────────────────────────── */
@media (max-width: 400px) {
  .vc-form-scroll { padding: 16px 12px 12px; }
  .vc-step-body { padding: 14px 12px; }
  .vc-step-heading { padding: 12px; }
  .vc-mh-badge { display: none; }
  .vc-welcome-card { padding: 24px 16px; }
  .vc-ref-card { padding: 24px 16px; }
}
</style>
